import { businessAccountPendingMembersQuery } from '@Admin/BusinessAccountDetailsPage/PendingMembersPanel/query'
import { Alert, message, Modal } from 'antd'
import { PropsWithChildren, FC, useState } from 'react'

import EmailAddressesForm from '~/apps/consumer/components/EmailAddressesForm'
import Box from '~/core/Box'
import Link from '~/core/Link'
import Text from '~/core/Text'
import { useRequestBusinessAccountInvitesMutation } from '~/gql/graphql'
import logger from '~/lib/logger'

interface RequestBusinessAccountInviteModalPropsType {
    visible: boolean
    onClose: () => void
    onFailedInvites: (failedEmails: string[]) => void
    businessAccountId: string
    businessAccountName: string
}

const RequestBusinessAccountInviteModal: FC<
    PropsWithChildren<RequestBusinessAccountInviteModalPropsType>
> = ({
    visible,
    onClose,
    businessAccountId,
    businessAccountName,
    onFailedInvites,
}) => {
    const [disabled, setDisabled] = useState<boolean>(true)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<boolean>(false)
    const [emails, setEmails] = useState<string[]>([])
    const [requestBusinessAccountInvites] =
        useRequestBusinessAccountInvitesMutation()

    const onAddMembers = async () => {
        if (error) {
            return null
        }

        setLoading(true)
        const variables = { businessAccountId, emails }

        try {
            const mutation = await requestBusinessAccountInvites({
                variables,
                refetchQueries: [businessAccountPendingMembersQuery],
            })

            const invites = mutation.data?.requestBusinessAccountInvites || []
            const failedInvites = invites.filter(invite => {
                return !!invite.failureReason
            })
            const successfulInvites = invites.filter(invite => {
                return !invite.failureReason
            })

            if (successfulInvites.length) {
                message.success(`${successfulInvites.length} invitations sent!`)
            }

            if (failedInvites.length) {
                const failedEmails = failedInvites.map(invite => invite.email)

                onFailedInvites(failedEmails)
            }

            onClose()
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'failed to request invitation(s) for businessAccount:  ',
                    variables,
                },
            })
            setLoading(false)
            message.error(
                'We encountered an error while sending the invitation request',
            )
        }
    }

    const onHandleFormChange = (change: {
        error: boolean
        value: string[]
    }) => {
        setError(!!change.error)
        if (!change.error) {
            setEmails(change.value)
        }
        setDisabled(!!change.error)
    }

    return (
        <Modal
            title={`Invite members to ${businessAccountName}`}
            okText="Invite"
            okButtonProps={{
                disabled,
                loading,
            }}
            visible={visible}
            onCancel={onClose}
            onOk={onAddMembers}
            width="550px"
            centered
            closable
            maskClosable={false}
        >
            <Box direction="col">
                <Alert
                    style={{ marginBottom: '13px' }}
                    message="Members in your organization will be able to:"
                    description={
                        <ul style={{ marginBottom: 0 }}>
                            <li>View and invite members to the account</li>
                            <li>
                                Access payment options and/or Premium features
                                (if enabled)
                            </li>
                        </ul>
                    }
                    type="info"
                />
                <EmailAddressesForm
                    onFormChanged={onHandleFormChange}
                    hasError={error}
                />
                <Text fontSize="14px" color="blackAlpha45" fontWeight={300}>
                    Note: Members are required to verify their email address to
                    join.
                </Text>
                <Text
                    fontSize="14px"
                    color="blackAlpha45"
                    fontWeight={500}
                    style={{ marginTop: '6px' }}
                >
                    <Link href={'/pages/my-account/team-members'}>
                        View all members in your organization
                    </Link>
                </Text>
            </Box>
        </Modal>
    )
}

export default RequestBusinessAccountInviteModal
